import { ACCESS_KEY, REACT_APP_BASE_URL_CORE, REACT_APP_BASE_URL_DEV_CORE, urlAPI } from '../../../../../../urls'
import { type AppDispatch, type RootState } from '../../../../../store'
import { fetchUser } from '../../../../userActions'
import { fetchUnits } from '../../../../unitActions/fetchUnits'

type AnyObj = Record<string, any>

const NOC_SERVICE_ID = 'noc_generation'
const NOC_SERVICE_NAME = 'Marketing Authorization Form'
const NOC_STATUS_SENT_FOR_SIGNATURE = 'Sent for Signature'
const BOLDSIGN_SIGNING_PROVIDER = 'boldsign'
const BOLDSIGN_PLATFORM_ID = 'BSO_CLUB'
const DEFAULT_FINDING_TENANT_DURATION_DAYS = 45
const FINDING_TENANT_DURATION_BY_PACKAGE: Array<{ label: string, days: number }> = [
    { label: '30 Days Plan', days: 30 },
    { label: '45 Days Plan', days: 45 },
    { label: '60 Days Plan', days: 60 }
]

// https://bso-club.com/api

const isDevMode = process.env.REACT_APP_IS_DEV === 'true'


const SEND_DOCUMENT_ENDPOINT = `${isDevMode ? REACT_APP_BASE_URL_DEV_CORE : REACT_APP_BASE_URL_CORE}/providers/boldsign/send_document`
const REF_NUMBER_REGEXP = /^FAT\d{6}\/U\d{3}$/
const toArray = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : [])
const asString = (value: unknown): string => String(value ?? '').trim()
const normalizeEmail = (value: unknown): string => String(value ?? '').trim().toLowerCase()
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
const toFiniteNumber = (value: unknown): number | null => {
    const num = Number(value)
    return Number.isFinite(num) ? num : null
}

const normalizeServiceToken = (value: unknown): string => {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/^services\./, '')
        .replace(/[\s-]+/g, '_')
}

const normalizePackageLabel = (value: unknown): string => {
    return String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
}

const getRelatedServiceId = (service: AnyObj): string => {
    const raw =
        service?.serviceId ??
        service?.ServiceID ??
        service?.ServiceId ??
        (typeof service?.id === 'string' ? service.id : undefined)

    return normalizeServiceToken(raw)
}

const normalizeOrder = (value: AnyObj): AnyObj | null => {
    if (value == null) return null
    if (value?.data?.attributes != null) return { id: value?.data?.id, ...value.data.attributes }
    if (value?.attributes != null) return { id: value?.id, ...value.attributes }
    return value
}

const readOrderById = async (orderId: number, jwt: string): Promise<AnyObj | null> => {
    try {
        const bodyObject = {
            method: 'GET',
            collection: 'orders',
            id: orderId,
            query: 'populate[RelatedServices]=*&populate[Unit][fields][0]=id'
        }
        const response = await fetch(`${urlAPI}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${jwt}`
            },
            body: JSON.stringify(bodyObject)
        })
        const dataJSON = await response.json()
        if (dataJSON?.success === true && dataJSON?.response?.error == null) return dataJSON?.response
        return null
    } catch {
        return null
    }
}

const unwrapEntity = (value: any): any => {
    if (value == null) return null
    if (Array.isArray(value?.data)) return value
    return value?.data ?? value
}

const unwrapList = <T,>(value: any): T[] => {
    if (value == null) return []
    if (Array.isArray(value)) return value as T[]
    if (Array.isArray(value?.data)) return value.data as T[]
    return [value as T]
}

const getNocUnitAttributes = (unit: AnyObj): AnyObj => {
    const entity = unwrapEntity(unit)
    return entity?.attributes ?? entity ?? {}
}

// A representative POA unit is identified from the landlord relation itself.
// Do not infer it from a contact email, selected signer, or current account.
export const isRepresentativePoaUnit = (unit: AnyObj): boolean => {
    const unitAttributes = getNocUnitAttributes(unit)
    return unwrapList<any>(unitAttributes?.Landlords).some((rawRelation) => {
        const relation = rawRelation?.attributes ?? rawRelation ?? {}
        return unwrapList(relation?.Representatives).length > 0
    })
}

const extractContact = (person: AnyObj | null): { name: string, email: string, id: number | null } => {
    const entity = unwrapEntity(person)
    const entityAttributes = entity?.attributes ?? entity ?? {}
    const contactEntity = unwrapEntity(entityAttributes?.ContactInfo)
    const contact = contactEntity?.attributes ?? contactEntity ?? {}
    const normalizeName = (value: unknown): string => asString(value).replace(/\s+/g, ' ')
    const firstName = normalizeName(contact?.FirstName ?? entityAttributes?.FirstName)
    const familyName = normalizeName(contact?.FamilyName ?? entityAttributes?.FamilyName)
    const fullName = normalizeName([firstName, familyName].filter(Boolean).join(' ')) || normalizeName(entityAttributes?.Name ?? entityAttributes?.FullName)
    const email = normalizeEmail(contact?.Email ?? entityAttributes?.ContactEmail ?? entityAttributes?.email)
    const idRaw = entity?.id ?? entity?.attributes?.id
    const id = Number.isFinite(Number(idRaw)) ? Number(idRaw) : null
    return { name: fullName, email, id }
}

type NocSigner = { signerName: string, signerEmail: string, source: string }
type ResolvedNocSigner = NocSigner & { signerLandlordId: number | null }

const resolveNocSignerWithLandlordId = (unitAttributes: AnyObj): ResolvedNocSigner | { error: string } => {
    const relations = unwrapList<any>(unitAttributes?.Landlords)

    // A decision maker is preferred only when the actual landlord relation has an email.
    for (let relationIndex = 0; relationIndex < relations.length; relationIndex += 1) {
        const relation = relations[relationIndex]
        if (relation?.IsDecisionMaker !== true) continue
        const owner = extractContact(relation?.Landlord)
        if (isValidEmail(owner.email)) {
            return {
                signerName: owner.name || 'Owner',
                signerEmail: owner.email,
                signerLandlordId: owner.id,
                source: `decisionMakerOwner[${relationIndex + 1}]`
            }
        }
    }

    for (let relationIndex = 0; relationIndex < relations.length; relationIndex += 1) {
        const owner = extractContact(relations[relationIndex]?.Landlord)
        if (isValidEmail(owner.email)) {
            return {
                signerName: owner.name || 'Owner',
                signerEmail: owner.email,
                signerLandlordId: owner.id,
                source: `owner[${relationIndex + 1}]`
            }
        }
    }

    return {
        error: 'No actual landlord email is available. Enter a confirmed landlord signer name and email to continue.'
    }
}

const extractUnitOwnerFallbackContact = (ownerUser: AnyObj | null): { name: string, email: string, id: number | null } => {
    const entity = unwrapEntity(ownerUser)
    const attributes = entity?.attributes ?? entity ?? {}
    const contactEntity = unwrapEntity(attributes?.ContactInfo)
    const contact = contactEntity?.attributes ?? contactEntity ?? {}
    const name = [asString(contact?.FirstName), asString(contact?.FamilyName)].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim()
    const email = normalizeEmail(contact?.Email ?? attributes?.ContactEmail ?? attributes?.email)
    const id = toFiniteNumber(entity?.id ?? entity?.attributes?.id)
    return { name, email, id }
}

const resolveUnitOwnerFallbackSigner = (unitAttributes: AnyObj): NocSigner | null => {
    if (isRepresentativePoaUnit(unitAttributes)) return null

    const ownerUser = extractUnitOwnerFallbackContact(unitAttributes?.NocOwnerUser)
    const unitOwner = extractContact(unitAttributes?.User)
    if (ownerUser.id != null && unitOwner.id != null && ownerUser.id !== unitOwner.id) return null
    if (ownerUser.name.length === 0 || !isValidEmail(ownerUser.email)) return null

    return {
        signerName: ownerUser.name,
        signerEmail: ownerUser.email,
        source: 'unitOwnerUser'
    }
}

export const resolveNocSigner = (unitAttributes: AnyObj): NocSigner | { error: string } => {
    const result = resolveNocSignerWithLandlordId(unitAttributes)
    if ('error' in result) return resolveUnitOwnerFallbackSigner(unitAttributes) ?? result
    const { signerLandlordId: _signerLandlordId, ...signer } = result
    return signer
}

const extractDocumentId = (payload: AnyObj): string | null => {
    const value = asString(payload?.document_id)
    if (value.length > 0) return value
    return null
}

const getPropertyEntity = (unitAttributes: AnyObj): AnyObj => {
    return unitAttributes?.Property?.data?.attributes ?? unitAttributes?.Property?.attributes ?? unitAttributes?.Property ?? {}
}

const resolveNocNotifyUserId = (unitAttributes: AnyObj): number | null => {
    const directUnitUser = extractContact(unitAttributes?.User)
    if (directUnitUser.id != null) return directUnitUser.id

    const relations = unwrapList<any>(unitAttributes?.Landlords)
    for (let index = 0; index < relations.length; index += 1) {
        const owner = extractContact(relations[index]?.Landlord)
        if (owner.id != null) return owner.id
    }
    return null
}

const getUnitDocuments = (unitAttributes: AnyObj): AnyObj[] => {
    const docs = unitAttributes?.Documents
    if (Array.isArray(docs)) return docs
    if (Array.isArray(docs?.data)) return docs.data
    return []
}

const getDocTypeParts = (doc: AnyObj): AnyObj[] => {
    const src = doc?.attributes ?? doc ?? {}
    const raw = src?.Type ?? []
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.data)) return raw.data
    return raw != null ? [raw] : []
}

const getDocTokens = (doc: AnyObj): string => {
    const src = doc?.attributes ?? doc ?? {}
    const typeTokens = getDocTypeParts(doc)
        .map((item: AnyObj) => `${asString(item?.__component)} ${asString(item?.Type)}`.toLowerCase())
        .join(' ')
    return `${typeTokens} ${asString(src?.Category).toLowerCase()} ${asString(src?.Name).toLowerCase()}`
}

const resolveTitleDeed = (unitAttributes: AnyObj): string => {
    const docs = getUnitDocuments(unitAttributes)
    const titleDeedDoc = docs.find((doc: AnyObj) => {
        const tokens = getDocTokens(doc)
        return tokens.includes('title deed') || tokens.includes('title-deed') || tokens.includes('oqood') || tokens.includes('ownership')
    })
    if (titleDeedDoc != null) {
        const src = titleDeedDoc?.attributes ?? titleDeedDoc ?? {}
        const typePart = getDocTypeParts(titleDeedDoc)?.[0] ?? {}
        const fromDoc = asString(typePart?.Number ?? src?.Number ?? src?.RefID)
        if (fromDoc.length > 0) return fromDoc
    }
    return ''
}

const resolveRefNumber = (orderId: number, unitId: number): string => {
    const orderPart = Math.abs(Math.trunc(Number(orderId))) % 1000000
    const unitPart = Math.abs(Math.trunc(Number(unitId))) % 1000
    return `FAT${String(orderPart).padStart(6, '0')}/U${String(unitPart).padStart(3, '0')}`
}

export interface NocPreparedSigner {
    email: string
    name: string
    portal_id?: string
}

export interface NocPreparedFatContract {
    type: 'FAT_NOC'
    ref_number: string
    landlord_name: string
    unit_location: string
    unit_developer: string
    unit_type: string
    unit_number: string
    plot_number: string
    title_deed: string
    unit_area: number | ''
    bedrooms: number | ''
    parking_lots: number | ''
}

export interface NocPreparedPayload {
    title: string
    signers: NocPreparedSigner[]
    contracts: NocPreparedFatContract[]
    metadata?: Record<string, unknown>
}

const buildFatNocRequestPayload = (
    unitAttributes: AnyObj,
    signer: NocSigner,
    orderId: number,
    unitId: number
): { payload: NocPreparedPayload } => {
    const property = getPropertyEntity(unitAttributes)
    const refNumber = resolveRefNumber(orderId, unitId)
    const unitNumberRaw = asString(unitAttributes?.Number)
    const unitType = asString(unitAttributes?.Type)
    const plotNumber = asString(property?.PlotNumber)
    const titleDeed = resolveTitleDeed(unitAttributes)
    const unitArea = toFiniteNumber(unitAttributes?.SizeArea)
    const bedrooms = toFiniteNumber(unitAttributes?.Bedrooms)
    const parkingLots = toFiniteNumber(unitAttributes?.ParkingSlotsAmount)
    const unitLocation = asString(property?.Address) || [property?.Area, property?.Community, property?.Name].map(asString).filter(Boolean).join(', ')
    const unitDeveloper = asString(property?.Developer)

    const contract: NocPreparedFatContract = {
        type: 'FAT_NOC',
        ref_number: refNumber,
        landlord_name: signer.signerName,
        unit_location: unitLocation,
        unit_developer: unitDeveloper,
        unit_type: unitType,
        unit_number: unitNumberRaw,
        plot_number: plotNumber,
        title_deed: titleDeed,
        unit_area: unitArea ?? '',
        bedrooms: bedrooms ?? '',
        parking_lots: parkingLots ?? ''
    }

    return {
        payload: {
            title: `${NOC_SERVICE_NAME} ${refNumber}`,
            signers: [{ email: signer.signerEmail, name: signer.signerName }],
            contracts: [contract],
            metadata: {
                serviceId: NOC_SERVICE_ID,
               // signerSource: signer.source,
                orderId,
                unitId
            }
            // "Error: End of data reached (data length = 0, asked index = 4). Corrupted zip ?"



        }
    }
}

interface TriggerNocSentForSignatureDTO {
    orderId: number
    unit: AnyObj
    preparedPayload?: NocPreparedPayload
}

interface PrepareNocSentForSignatureDraftDTO {
    orderId: number
    unit: AnyObj
}

const isValidLandlordId = (value: unknown): boolean => {
    const id = asString(value)
    return /^\d+$/.test(id) && Number(id) > 0
}

const validateNocPreparedPayload = (payload: NocPreparedPayload, isPoaUnit: boolean): string | null => {
    const title = asString(payload?.title)
    const signers = Array.isArray(payload?.signers) ? payload.signers : []
    const contracts = Array.isArray(payload?.contracts) ? payload.contracts : []
    const signer = signers[0] ?? {}
    const contract = contracts[0] ?? {}

    if (title.length === 0) return 'title is required'
    if (signers.length === 0) return 'at least one signer is required'
    if (asString((signer as NocPreparedSigner).name).length === 0) return 'signer name is required'
    if (!isValidEmail(normalizeEmail((signer as NocPreparedSigner).email))) return 'signer email is invalid'
    if (isPoaUnit && !isValidLandlordId((signer as NocPreparedSigner).portal_id)) return 'POA landlord portal_id is missing or invalid'
    if (isPoaUnit && payload?.metadata?.send_email !== 'true') return "POA metadata.send_email must be 'true'"
    if (isPoaUnit && !isValidEmail(normalizeEmail(payload?.metadata?.sales_team_email))) return 'POA metadata.sales_team_email is invalid'
    if (contracts.length === 0) return 'contracts[0] is required'
    if ((contract as NocPreparedFatContract).type !== 'FAT_NOC') return 'contracts[0].type must be FAT_NOC'
    if (!REF_NUMBER_REGEXP.test(asString((contract as NocPreparedFatContract).ref_number))) return 'contracts[0].ref_number has invalid format'
    if (asString((contract as NocPreparedFatContract).landlord_name).length === 0) return 'contracts[0].landlord_name is required'
    if (asString((contract as NocPreparedFatContract).unit_location).length === 0) return 'contracts[0].unit_location is required'
    if (asString((contract as NocPreparedFatContract).unit_developer).length === 0) return 'contracts[0].unit_developer is required'
    if (asString((contract as NocPreparedFatContract).unit_type).length === 0) return 'contracts[0].unit_type is required'
    if (asString((contract as NocPreparedFatContract).unit_number).length === 0) return 'contracts[0].unit_number is required'
    if (asString((contract as NocPreparedFatContract).plot_number).length === 0) return 'contracts[0].plot_number is required'
    if (asString((contract as NocPreparedFatContract).title_deed).length === 0) return 'contracts[0].title_deed is required'
    if (toFiniteNumber((contract as NocPreparedFatContract).unit_area) == null) return 'contracts[0].unit_area must be a number'
    if (toFiniteNumber((contract as NocPreparedFatContract).bedrooms) == null) return 'contracts[0].bedrooms must be a number'
    if (toFiniteNumber((contract as NocPreparedFatContract).parking_lots) == null) return 'contracts[0].parking_lots must be a number'
    return null
}

const withNocPayloadMetadata = (
    payload: NocPreparedPayload,
    signerSource: string,
    orderId: number,
    unitId: number,
    userId: number | null
): NocPreparedPayload => {
    const isDev = process.env.REACT_APP_IS_DEV === 'true'
    return {
        ...payload,
        metadata: {
            ...(payload?.metadata ?? {}),
            serviceId: String(NOC_SERVICE_ID),
            signerSelection: payload?.metadata?.signerSelection ?? (signerSource === 'unresolved' ? 'unresolved' : 'automatic_landlord'),
            //  signerSource: String(signerSource),
            orderId: String(orderId),
            unitId: String(unitId),
            userId: String(userId ?? ''),
            isDev: String(isDev)
        }
    }
}

export const enrichNocPayloadForRepresentativePoa = (
    payload: NocPreparedPayload,
    unit: AnyObj
): { payload: NocPreparedPayload } | { error: string } => {
    const unitAttributes = getNocUnitAttributes(unit)
    if (!isRepresentativePoaUnit(unitAttributes)) return { payload }

    const signerResult = resolveNocSignerWithLandlordId(unitAttributes)
    if ('error' in signerResult) return { error: signerResult.error }
    if (signerResult.signerLandlordId == null || signerResult.signerLandlordId <= 0) {
        return { error: 'POA unit cannot be sent for signature because the selected landlord ID is missing.' }
    }

    const signers = Array.isArray(payload.signers) ? [...payload.signers] : []
    if (signers.length === 0) return { error: 'POA unit cannot be sent for signature because no signer is available.' }

    signers[0] = {
        ...signers[0],
        name: signerResult.signerName,
        email: signerResult.signerEmail,
        portal_id: String(signerResult.signerLandlordId)
    }

    return {
        payload: {
            ...payload,
            signers,
            metadata: {
                ...(payload.metadata ?? {}),
                send_email: 'true',
                sales_team_email: 'operations@bso.ae'
            }
        }
    }
}

export const buildBoldSignSnakeCaseMetadata = (metadata: Record<string, unknown> = {}, isPoaUnit: boolean = false): Record<string, unknown> => {
    const result: Record<string, unknown> = {
        service_id: metadata.service_id ?? metadata.serviceId,
        order_id: metadata.order_id ?? metadata.orderId,
        unit_id: metadata.unit_id ?? metadata.unitId,
        user_id: metadata.user_id ?? metadata.userId,
        is_dev: metadata.is_dev ?? metadata.isDev,
        platform_id: BOLDSIGN_PLATFORM_ID
    }

    const serviceStartDate = metadata.service_start_date ?? metadata.serviceStartDate
    const serviceEndDate = metadata.service_end_date ?? metadata.serviceEndDate

    if (serviceStartDate != null && String(serviceStartDate).trim().length > 0) {
        result.service_start_date = serviceStartDate
    }

    if (serviceEndDate != null && String(serviceEndDate).trim().length > 0) {
        result.service_end_date = serviceEndDate
    }

    if (isPoaUnit && metadata.send_email === 'true' && isValidEmail(normalizeEmail(metadata.sales_team_email))) {
        result.send_email = 'true'
        result.sales_team_email = normalizeEmail(metadata.sales_team_email)
    }

    const unit = getMeaningfulUnitAddress(metadata.unit)
    if (unit != null) result.unit = unit

    return result
}

const getMeaningfulUnitAddress = (value: unknown): string | null => {
    const unit = asString(value).replace(/\s+/g, ' ')
    const placeholder = unit.toLowerCase()
    if (unit.length === 0 || /^\d+$/.test(unit)) return null
    if (['unit address', 'unit adress', 'address', 'n/a', 'na', 'null', 'undefined', '-', '—'].includes(placeholder)) return null
    return unit
}

const toIsoDateOnly = (value: unknown): string | undefined => {
    const raw = asString(value)
    if (raw.length === 0) return undefined
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return undefined
    return date.toISOString().slice(0, 10)
}

const addUtcDays = (isoDate: string, days: number): string => {
    const date = new Date(`${isoDate}T00:00:00.000Z`)
    date.setUTCDate(date.getUTCDate() + days)
    return date.toISOString().slice(0, 10)
}

const resolveFindingTenantDurationDaysFromOrderPackage = (order: AnyObj): number => {
    const packageToken = normalizePackageLabel(order?.Package)
    const match = FINDING_TENANT_DURATION_BY_PACKAGE.find((item) => (
        normalizePackageLabel(item.label) === packageToken
    ))
    return match?.days ?? DEFAULT_FINDING_TENANT_DURATION_DAYS
}

const resolveOrderDateOnly = (order: AnyObj): string | null => {
    return toIsoDateOnly(
        order?.createdAt ??
        order?.CreatedAt ??
        order?.InvoiceDate ??
        order?.invoiceDate ??
        order?.updatedAt ??
        order?.UpdatedAt
    ) ?? null
}

const resolveFindingTenantMetadataDates = (order: AnyObj): { service_start_date: string, service_end_date: string } | null => {
    const serviceStartDate = resolveOrderDateOnly(order)
    if (serviceStartDate == null) return null

    const durationDays = resolveFindingTenantDurationDaysFromOrderPackage(order)

    return {
        service_start_date: serviceStartDate,
        service_end_date: addUtcDays(serviceStartDate, durationDays)
    }
}

export const prepareNocSentForSignatureDraft = ({ orderId, unit }: PrepareNocSentForSignatureDraftDTO) =>
    async (): Promise<{ isSuccessful: boolean, textNotification: string, draft?: NocPreparedPayload }> => {
        const unitEntity = unwrapEntity(unit) ?? {}
        const unitAttributes = getNocUnitAttributes(unit)
        const signerResult = resolveNocSigner(unitAttributes)
        const signer: NocSigner = 'error' in signerResult
            ? { signerName: '', signerEmail: '', source: 'unresolved' }
            : signerResult
        const unitIdRaw = Number(unitEntity?.id ?? unitAttributes?.id)
        const unitId = Number.isFinite(unitIdRaw) && unitIdRaw > 0 ? unitIdRaw : 0
        const metadataUserId = resolveNocNotifyUserId(unitAttributes)
        const payloadBuild = buildFatNocRequestPayload(unitAttributes, signer, Number(orderId), unitId)
        const draftPayload = withNocPayloadMetadata(payloadBuild.payload, signer.source, Number(orderId), unitId, metadataUserId)
        const poaEnrichment = enrichNocPayloadForRepresentativePoa(draftPayload, unit)
        if ('error' in poaEnrichment) {
            return { isSuccessful: false, textNotification: poaEnrichment.error }
        }
        return {
            isSuccessful: true,
            textNotification: 'error' in signerResult ? signerResult.error : '',
            draft: poaEnrichment.payload
        }
    }

export const triggerNocSentForSignature = ({ orderId, unit, preparedPayload }: TriggerNocSentForSignatureDTO) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<{ isSuccessful: boolean, textNotification: string }> => {
        const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''
        const currentUser = getState().user.currentUser as AnyObj
        const unitEntity = unwrapEntity(unit) ?? {}
        const unitAttributes = getNocUnitAttributes(unit)
        const orderFromState = toArray<any>(currentUser?.Orders).find((item: AnyObj) => Number(item?.id) === Number(orderId)) ?? null
        const orderRaw = orderFromState ?? await readOrderById(orderId, jwt)
        const order = normalizeOrder(orderRaw)
        if (order == null) {
            return { isSuccessful: false, textNotification: 'Order was not found.' }
        }

        const relatedServices = toArray<any>(order?.RelatedServices)
        if (!Array.isArray(order?.RelatedServices)) {
            return { isSuccessful: false, textNotification: 'Top-level RelatedServices are missing. Migration required before sending NOC.' }
        }
        const now = new Date().toISOString()
        const unitId = Number(unitEntity?.id ?? unitAttributes?.id ?? order?.Unit?.id ?? order?.Unit?.data?.id)
        const metadataUserId = resolveNocNotifyUserId(unitAttributes)
        if (!Number.isFinite(unitId) || unitId <= 0) {
            return { isSuccessful: false, textNotification: 'Unit ID is missing for NOC signing request.' }
        }

        const existingNoc = relatedServices.find((item: AnyObj) => getRelatedServiceId(item) === NOC_SERVICE_ID) ?? null
        const existingDocumentId = asString(existingNoc?.documentId)
        if (existingDocumentId.length > 0) {
            return {
                isSuccessful: false,
                textNotification: `NOC document already exists (documentId: ${existingDocumentId}).`
            }
        }

        let finalPayload: NocPreparedPayload | null = null
        const isPoaUnit = isRepresentativePoaUnit(unit)
        let signerSource = 'admin.preparedPayload'
        if (preparedPayload != null) {
            finalPayload = withNocPayloadMetadata(preparedPayload, signerSource, Number(orderId), unitId, metadataUserId)
        } else {
            const signerResult = resolveNocSigner(unitAttributes)
            if ('error' in signerResult) {
                return { isSuccessful: false, textNotification: signerResult.error }
            }
            signerSource = signerResult.source
            const payloadBuild = buildFatNocRequestPayload(unitAttributes, signerResult, Number(orderId), unitId)
            finalPayload = withNocPayloadMetadata(payloadBuild.payload, signerSource, Number(orderId), unitId, metadataUserId)
        }

        if (finalPayload == null) {
            return { isSuccessful: false, textNotification: 'NOC contract payload could not be prepared.' }
        }
        const poaEnrichment = enrichNocPayloadForRepresentativePoa(finalPayload, unit)
        if ('error' in poaEnrichment) {
            return { isSuccessful: false, textNotification: poaEnrichment.error }
        }
        finalPayload = poaEnrichment.payload
        const validationError = validateNocPreparedPayload(finalPayload, isPoaUnit)
        if (validationError != null) {
            return { isSuccessful: false, textNotification: `NOC contract payload validation failed: ${validationError}.` }
        }

        const sentSigner = finalPayload.signers[0]

        let documentId: string | null = null
        try {
            const findingTenantMetadataDates = resolveFindingTenantMetadataDates(order)
            if (findingTenantMetadataDates == null) {
                return {
                    isSuccessful: false,
                    textNotification: 'Finding Tenant order date could not be resolved for BoldSign metadata.'
                }
            }
            const boldSignPayload: NocPreparedPayload = {
                ...finalPayload,
                metadata: buildBoldSignSnakeCaseMetadata({
                    ...(finalPayload.metadata ?? {}),
                    unit: finalPayload.contracts?.[0]?.unit_location,
                    ...findingTenantMetadataDates
                }, isPoaUnit)
            }
            const response = await fetch(SEND_DOCUMENT_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                 //   ...getBsoApiHeaders(),
                    ...(jwt ? { Authorization: `Bearer ${jwt}` } : {})
                },
                body: JSON.stringify(boldSignPayload)
            })
            const responseJSON = await response.json()
            if (!response.ok) {
                const reason = asString(responseJSON?.message ?? responseJSON?.error ?? response.statusText ?? 'Unknown error')
                return { isSuccessful: false, textNotification: `Failed to create NOC signing document: ${reason}` }
            }
            documentId = extractDocumentId(responseJSON)
            if (documentId == null) {
                return { isSuccessful: false, textNotification: 'Failed to create NOC signing document: missing documentId in response.' }
            }
        } catch {
            return { isSuccessful: false, textNotification: 'Failed to create NOC signing document.' }
        }

        const nocIndex = relatedServices.findIndex((item: AnyObj) => getRelatedServiceId(item) === NOC_SERVICE_ID)
        const existingNocItem = nocIndex >= 0 ? relatedServices[nocIndex] : {}
        const nextNocItem = {
            ...existingNocItem,
            id: NOC_SERVICE_ID,
            serviceId: NOC_SERVICE_ID,
            Name: asString(existingNocItem?.Name) || NOC_SERVICE_NAME,
            Status: NOC_STATUS_SENT_FOR_SIGNATURE,
            Notify: true,
            createdAt: existingNocItem?.createdAt ?? now,
            updatedAt: now,
            documentId,
            signerEmail: sentSigner.email,
            signerName: sentSigner.name,
            signingProvider: BOLDSIGN_SIGNING_PROVIDER
        }

        const nextRelatedServices = nocIndex >= 0
            ? relatedServices.map((item: AnyObj, index: number) => (index === nocIndex ? nextNocItem : item))
            : [...relatedServices, nextNocItem]

        try {
            const response = await fetch(`${urlAPI}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${jwt}`
                },
                body: JSON.stringify({
                    method: 'PUT',
                    collection: 'orders',
                    id: orderId,
                    body: { RelatedServices: nextRelatedServices }
                })
            })
            const responseJSON = await response.json() as AnyObj
            const isSuccessful = responseJSON?.response?.error == null
            if (!isSuccessful) {
                return { isSuccessful: false, textNotification: 'Document created but failed to update NOC related service.' }
            }

            await Promise.all([
                dispatch(fetchUser(true)),
                dispatch(fetchUnits(true))
            ])

            const notifyUserId = resolveNocNotifyUserId(unitAttributes)
            if (notifyUserId != null) {
                try {
                    // await sendNotification(
                    //     notifyDelay(),
                    //     notifyUserId,
                    //     Number(orderId),
                    //     'landlord-noc-ready-for-signature',
                    //     'Your Marketing Authorization Form is ready for signature. Please open it and sign it.'
                    // )
                } catch {
                    // keep NOC creation flow successful even if notification fails
                }
            }
            return { isSuccessful: true, textNotification: '' }
        } catch {
            return { isSuccessful: false, textNotification: 'Document created but failed to update NOC related service.' }
        }
    }
